/*eslint-disable*/
import React, { useRef, useState } from 'react';
import { useEditor, useNode } from '@craftjs/core';
import { useSelector } from 'react-redux';
import styles from './styles/styles.module.css';
import { useInteractionMode } from '../contexts/InteractionMode';
import { useStagedUploads } from '../../contexts/StagedUploadsContext';

const UploadArea = ({
  label,
  value, // store just the filename/path (string)
  left = 0,
  top = 0,
  width = 260,
  height = 80,
}: any) => {
  const { enabled, actions } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const interactionMode = useInteractionMode();
  const {
    connectors: { connect },
    setProp,
    id,
  } = useNode();

  const { selected } = useSelector((s: any) => s.formToggle); // 'FORM' | 'REPORT'
  const isView = interactionMode === 'VIEW';
  const isForm = selected === 'FORM';
  const isFill = interactionMode === 'FILL';

  // ✅ context hook
  const { staged, setFile } = useStagedUploads();

  const classes = React.useMemo(() => {
    switch (interactionMode) {
      case 'VIEW':
        return {
          wrapper: styles.wrapperReport,
          label: styles.labelReport,
          inputContainer: styles.inputContainerReport,
          row: styles.rowReport,
          link: styles.mockLink,
        };
      case 'BUILD':
      case 'FILL':
        return {
          wrapper: styles.wrapperForm,
          label: styles.labelForm,
          inputContainer: styles.inputContainerForm,
          row: styles.rowReport,
          link: styles.mockLink,
        };
      default:
        return {
          wrapper: '',
          label: '',
          inputContainer: '',
          row: '',
          link: '',
        };
    }
  }, [interactionMode]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setDragging] = useState(false);
  const [isResizing, setResizing] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [isDragOver, setDragOver] = useState(false);

  const stagedFileForThisNode = staged.get(id); // Map lookup
  const displayName = stagedFileForThisNode?.name || value || '—';

  const onMouseDown = (e: React.MouseEvent) => {
    if (!enabled || showEditor || isResizing) return;
    e.preventDefault();
    setDragging(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const initialLeft = left;
    const initialTop = top;
    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setProp((p: any) => {
        p.left = initialLeft + dx;
        p.top = initialTop + dy;
      });
    };
    const onUp = () => {
      setDragging(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const onResizeMouseDown = (e: React.MouseEvent) => {
    if (!enabled || !isForm) return;
    e.stopPropagation();
    e.preventDefault();
    setResizing(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const initialWidth = width;
    const initialHeight = height;
    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setProp((p: any) => {
        p.width = Math.max(120, initialWidth + dx);
        p.height = Math.max(50, initialHeight + dy);
      });
    };
    const onUp = () => {
      setResizing(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const onDoubleClick = () => {
    if (!enabled) return;
    if (isForm) setShowEditor(true);
  };

  const acceptFile = (file: File) => {
    setFile(id, file); // stage in context
    setProp((p: any) => {
      p.value = file.name; // keep filename in node props
    });
  };

  return (
    <div
      ref={(ref) => ref && connect(ref)}
      className={`${classes.wrapper} ${isDragging || isResizing ? styles.isActive : ''}`}
      style={{
        left,
        top,
        position: 'absolute',
        cursor: enabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
      }}
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      role={enabled ? 'button' : undefined}
      tabIndex={enabled ? 0 : undefined}
      data-mode={interactionMode}
    >
      {enabled && isForm && (
        <div
          className={styles.deleteButton}
          onClick={() => {
            setFile(id, null); // unstage
            actions.delete(id);
          }}
        >
          ×
        </div>
      )}

      {isView ? (
        <div className={classes.row} style={{ width, height }}>
          <a
            href="#"
            className={classes.link}
            onClick={(e) => e.preventDefault()}
            title={displayName}
          >
            Download file: {displayName}
          </a>
        </div>
      ) : (
        <>
          {label && <label className={classes.label}>{label}</label>}

          <div className={classes.inputContainer} style={{ width, height }}>
            <div
              className={`${styles.dropzone} ${isDragOver ? styles.dragging : ''}`}
              onClick={() => {
                if (isFill) fileInputRef.current?.click(); // only active in FILL
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const file = e.dataTransfer.files?.[0];
                if (file) acceptFile(file);
              }}
            >
              <p className={styles.text}>
                {stagedFileForThisNode
                  ? stagedFileForThisNode.name
                  : 'Drop files here or click to upload'}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                className={styles.hiddenInput}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) acceptFile(file);
                }}
              />
            </div>

            {enabled && isForm && (
              <div
                onMouseDown={onResizeMouseDown}
                className={styles.resizeHandle}
                title="Drag to resize"
              />
            )}
          </div>
        </>
      )}

      {showEditor && isForm && (
        <div
          className={styles.editor}
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <div>
            <label>Label:</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setProp((p: any) => (p.label = e.target.value))}
            />
          </div>
          <button onClick={() => setShowEditor(false)}>Done</button>
        </div>
      )}
    </div>
  );
};

(UploadArea as any).craft = {
  displayName: 'UploadArea',
  props: {
    label: 'Upload File',
    value: '',
    left: 0,
    top: 0,
    width: 260,
    height: 80,
  },
};

export default UploadArea;
